// src/firebase/submissions.js
// Submissions Service - handles homework submissions

import {
  collection,
  doc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  setDoc,
  deleteDoc,
  arrayUnion,
  increment,
  runTransaction
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, storage } from './config';
import { incrementMarkerStats } from './stats';
import { DEFAULT_ORG_ID, getCurrentUserOrgId } from './orgs';

/**
 * Create a new homework submission or add file to existing pending submission
 */
export const createSubmission = async (data) => {
  try {
    const { studentId, studentName, studentEmail, studentPhone, cohortId, cohortName, homeworkType, file, onProgress, examDurationMinutes, examExtraTimeApplied, submittedVia, spreadsheetAnswers, spreadsheetMarkingMode, spreadsheetSourceFilePath } = data;

    // Denormalize the cohort's org onto the submission, since marker/head
    // marker read access is org-scoped in firestore.rules - falls back to
    // the default org on any lookup failure so a submission is never
    // blocked from being created. Resolved up front (not just at doc-create
    // time below) because the existence-check query right below also needs
    // it: without an orgId filter, Firestore can't verify the org-scoped
    // read rule for a caller who isn't the student themselves (e.g. a head
    // marker uploading on a student's behalf), and rejects the query
    // outright with "Missing or insufficient permissions".
    let orgId = DEFAULT_ORG_ID;
    try {
      const cohortDoc = await getDoc(doc(db, 'cohorts', cohortId));
      if (cohortDoc.exists() && cohortDoc.data().orgId) {
        orgId = cohortDoc.data().orgId;
      }
    } catch (orgLookupError) {
      console.warn('Could not resolve cohort org, defaulting:', orgLookupError);
    }

    // Check if submission already exists for this student + homework type
    // NOTE: We don't use orderBy to avoid needing a composite index
    const existingQuery = query(
      collection(db, 'submissions'),
      where('studentId', '==', studentId),
      where('homeworkType', '==', homeworkType),
      where('cohortId', '==', cohortId),
      where('orgId', '==', orgId)
    );

    const existingSnapshot = await getDocs(existingQuery);

    // Unique file path per upload. Uses a client-generated Firestore ID
    // (no network round-trip) instead of Date.now() so two uploads can
    // never collide on the same storage path, even if fired in the same
    // millisecond.
    const fileName = file.name;
    const uniqueSuffix = doc(collection(db, 'submissions')).id;
    const filePath = `submissions/${cohortId}/${studentId}/${homeworkType}_${uniqueSuffix}.pdf`;
    const fileRef = ref(storage, filePath);

    await new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(fileRef, file);
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          if (onProgress && snapshot.totalBytes) {
            onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
          }
        },
        reject,
        resolve
      );
    });

    // Check if we have an existing PENDING submission to append this file to,
    // excluding soft-deleted ones. This is the ONLY status where a direct
    // append into originalFilePaths/fileNames happens - nothing has been
    // marked yet, so there's no risk of shifting page numbers under existing
    // annotations.
    const pendingSubmission = existingSnapshot.docs.find(
      doc => {
        const data = doc.data();
        return data.status === 'pending' && !data.deletedByStudent;
      }
    );

    // Once a submission has been flagged for review, claimed, is awaiting
    // review, or is marked, a further upload must NOT touch the real file
    // list - it goes into quarantine until a head marker explicitly releases
    // it (see releasePendingAdditions). This is what closes the gap that let
    // a student's late page silently land in an already-marked submission's
    // file list and desync the marker's existing annotations.
    const quarantineTarget = existingSnapshot.docs.find(
      doc => {
        const data = doc.data();
        return ['flagged_for_review', 'assigned', 'marking', 'awaiting_review', 'marked', 'rejected'].includes(data.status) && !data.deletedByStudent;
      }
    );

    if (pendingSubmission) {
      // ADD FILE TO EXISTING PENDING SUBMISSION
      // arrayUnion/increment are server-side field transforms, so this is
      // safe even if two uploads for the same submission land concurrently -
      // unlike a client-side read-modify-write of the arrays, neither write
      // can clobber the other.
      await updateDoc(pendingSubmission.ref, {
        originalFilePaths: arrayUnion(filePath),
        fileNames: arrayUnion(fileName),
        fileCount: increment(1),
        submittedAt: serverTimestamp(), // Update submission time
        ...(spreadsheetAnswers ? { spreadsheetAnswers, hasInteractiveSpreadsheet: true } : {}),
        ...(spreadsheetAnswers && spreadsheetMarkingMode ? { spreadsheetMarkingMode } : {}),
        ...(spreadsheetSourceFilePath ? { spreadsheetSourceFilePath } : {})
      });

      return { id: pendingSubmission.id, quarantined: false };

    } else if (quarantineTarget) {
      // QUARANTINE - hold the file on the same submission doc without
      // touching originalFilePaths, status, or fileCount. Nothing about the
      // live/marked submission changes until a head marker releases it.
      await updateDoc(quarantineTarget.ref, {
        pendingAdditionFilePaths: arrayUnion(filePath),
        pendingAdditionFileNames: arrayUnion(fileName),
        hasPendingAdditions: true,
        pendingAdditionAt: serverTimestamp()
      });

      // Notify the head marker (non-blocking)
      try {
        const targetData = quarantineTarget.data();
        const { sendDirectNotification, NOTIFICATION_TYPES } = await import('./headMarkerNotifications');
        const { getHomeworkNames } = await import('./settings');
        const homeworkNames = await getHomeworkNames();
        sendDirectNotification(NOTIFICATION_TYPES.ADDITIONAL_PAGES_PENDING, {
          studentName,
          cohortName: cohortName || '',
          homeworkType: homeworkNames[homeworkType] || homeworkType,
          submittedAt: new Date().toLocaleString('en-GB', { timeZone: 'Europe/London' }),
          currentStatus: targetData.status,
          markerName: targetData.markerName || ''
        });
      } catch (e) { console.error('Additional pages pending notification error:', e); }

      return { id: quarantineTarget.id, quarantined: true };

    } else {
      // CREATE NEW SUBMISSION (no pending submission exists, or previous one is being marked)

      // NEW: Fetch deadline for this homework type from cohort
      let homeworkDeadline = null;
      let isLate = false;
      let minutesLate = 0;
      
      try {
        const deadlineDoc = await getDoc(doc(db, 'cohorts', cohortId, 'deadlines', 'current'));
        if (deadlineDoc.exists()) {
          const deadlines = deadlineDoc.data();
          homeworkDeadline = deadlines[homeworkType];
          
          if (homeworkDeadline) {
            const now = Timestamp.now();
            const deadlineTime = homeworkDeadline.toMillis();
            const submissionTime = now.toMillis();
            
            if (submissionTime > deadlineTime) {
              isLate = true;
              minutesLate = Math.floor((submissionTime - deadlineTime) / (1000 * 60));
            }
          }
        }
      } catch (deadlineError) {
        console.warn('Could not fetch deadline:', deadlineError);
        // Continue without deadline check if there's an error
      }
      
      const deadline = new Date();
      deadline.setHours(deadline.getHours() + 48);

      // Deterministic doc ID (keyed off how many submissions already exist
      // for this student+homework+cohort combo) instead of addDoc's random
      // ID. If a second upload races this one before either has committed
      // its write, both target the SAME document - Firestore serializes
      // writes to a single doc, and arrayUnion/increment merge the files
      // into one submission instead of creating two "pending" duplicates.
      // If this ID ever did resolve to an existing non-pending doc (e.g. a
      // stale local count), the security rules reject the write outright
      // (updates to submissions require status == 'pending'), so this can
      // never silently overwrite a graded submission - it just fails loudly
      // and the upload can be retried.
      const pendingId = `${cohortId}_${studentId}_${homeworkType}_${existingSnapshot.size}`;
      const docRef = doc(db, 'submissions', pendingId);

      await setDoc(docRef, {
        studentId,
        studentName,
        studentEmail,
        studentPhone: studentPhone || '',
        cohortId,
        cohortName: cohortName || '',
        orgId,
        homeworkType,
        status: 'pending',
        assignedTo: null,
        submittedAt: serverTimestamp(),
        deadline: Timestamp.fromDate(deadline),
        homeworkDeadline: homeworkDeadline, // Store the deadline for reference
        isLate: isLate,
        minutesLate: minutesLate,
        markedAt: null,
        turnaroundHours: null,
        originalFilePaths: arrayUnion(filePath),
        fileNames: arrayUnion(fileName),
        fileCount: increment(1),
        markedFilePath: null,
        score: null,
        ...(examDurationMinutes != null ? { examDurationMinutes } : {}),
        ...(examExtraTimeApplied ? { examExtraTimeApplied } : {}),
        ...(submittedVia ? { submittedVia } : {}),
        ...(spreadsheetAnswers ? { spreadsheetAnswers, hasInteractiveSpreadsheet: true } : {}),
        ...(spreadsheetAnswers && spreadsheetMarkingMode ? { spreadsheetMarkingMode } : {}),
        ...(spreadsheetSourceFilePath ? { spreadsheetSourceFilePath } : {})
      }, { merge: true });

      // Send late submission notification if applicable (non-blocking)
      if (isLate) {
        try {
          const { sendDirectNotification, NOTIFICATION_TYPES } = await import('./headMarkerNotifications');
          const { getHomeworkNames } = await import('./settings');
          const homeworkNames = await getHomeworkNames();
          sendDirectNotification(NOTIFICATION_TYPES.LATE_SUBMISSION, {
            studentName,
            cohortName: cohortName || '',
            homeworkType: homeworkNames[homeworkType] || homeworkType,
            submittedAt: new Date().toLocaleString('en-GB', { timeZone: 'Europe/London' }),
            deadline: homeworkDeadline ? homeworkDeadline.toDate().toLocaleString('en-GB', { timeZone: 'Europe/London' }) : 'N/A'
          });
        } catch (e) { console.error('Late submission notification error:', e); }
      }
      
      // Send notification webhook (per-org, empty/unset by default - see settings.js)
      try {
        const { getSubmissionWebhookUrl } = await import('./settings');
        const webhookUrl = await getSubmissionWebhookUrl(orgId);
        if (webhookUrl) {
          await fetch(webhookUrl, {
            method: 'POST',
            mode: 'no-cors',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              homeworkType: homeworkType,
              studentName: studentName
            })
          });
        }
      } catch (notifError) {
        console.error('Notification webhook error:', notifError);
      }

      return { id: docRef.id, quarantined: false };
    }
  } catch (error) {
    console.error('Create submission error:', error);
    throw error;
  }
};

/**
 * Upload a submission on behalf of a student (head marker only - e.g. student emailed the
 * file directly, or their own upload was corrupted and needs replacing). Only allowed when
 * the student has no valid submission for this homework type yet; if one exists it must be
 * reset first via resetSubmissionForResubmit. Delegates to createSubmission for the actual
 * upload/doc logic once that guard passes.
 */
export const headMarkerUploadSubmission = async (data) => {
  const { studentId, cohortId, homeworkType } = data;

  // orgId filter required for the same reason as createSubmission's own
  // existence check: the caller here is a head marker, not the student, so
  // only the org-scoped read rule can authorize this query - and Firestore
  // needs it as an explicit query constraint to verify that, or it rejects
  // the query outright with "Missing or insufficient permissions".
  let orgId = DEFAULT_ORG_ID;
  try {
    const cohortDoc = await getDoc(doc(db, 'cohorts', cohortId));
    if (cohortDoc.exists() && cohortDoc.data().orgId) {
      orgId = cohortDoc.data().orgId;
    }
  } catch (orgLookupError) {
    console.warn('Could not resolve cohort org, defaulting:', orgLookupError);
  }

  const existingQuery = query(
    collection(db, 'submissions'),
    where('studentId', '==', studentId),
    where('homeworkType', '==', homeworkType),
    where('cohortId', '==', cohortId),
    where('orgId', '==', orgId)
  );
  const existingSnapshot = await getDocs(existingQuery);
  const hasValidSubmission = existingSnapshot.docs.some(d => (d.data().fileCount || 0) > 0);

  if (hasValidSubmission) {
    throw new Error('This student already has a submission for this homework type. Reset it first if it needs replacing.');
  }

  return createSubmission(data);
};

/**
 * Delete a file from a pending submission
 */
export const deleteFileFromSubmission = async (submissionId, fileIndex, filePath) => {
  try {
    const docRef = doc(db, 'submissions', submissionId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Submission not found');
    }
    
    const data = docSnap.data();
    
    // Only allow deletion if status is pending
    if (data.status !== 'pending') {
      throw new Error('Cannot delete files from a submission that is being marked');
    }
    
    // Remove file from Storage
    try {
      const fileRef = ref(storage, filePath);
      await deleteObject(fileRef);
    } catch (storageError) {
      console.warn('File may have already been deleted from storage:', storageError);
    }
    
    // Handle both old format (URLs) and new format (paths)
    const updatedFilePaths = [...(data.originalFilePaths || data.originalFileUrls || [])];
    const updatedFileNames = [...data.fileNames];
    
    updatedFilePaths.splice(fileIndex, 1);
    updatedFileNames.splice(fileIndex, 1);
    
    // If no files left, delete the entire submission
    if (updatedFilePaths.length === 0) {
      await deleteDoc(docRef);
      console.log('Submission deleted (no files remaining)');
      return { deleted: true };
    }
    
    // Update submission with remaining files
    await updateDoc(docRef, {
      originalFilePaths: updatedFilePaths,
      fileNames: updatedFileNames,
      fileCount: updatedFilePaths.length
    });
    
    console.log('File deleted from submission:', submissionId);
    return { deleted: false };
  } catch (error) {
    console.error('Delete file error:', error);
    throw error;
  }
};

/**
 * Unclaim a submission — returns it to the available pool (status: pending)
 */
export const unclaimSubmission = async (submissionId) => {
  try {
    const docRef = doc(db, 'submissions', submissionId);
    await updateDoc(docRef, {
      status: 'pending',
      assignedTo: null,
      markerName: null,
      claimedBy: null,
      claimedAt: null
    });
    return true;
  } catch (error) {
    console.error('Unclaim submission error:', error);
    throw error;
  }
};

/**
 * Flag a submission for head marker review — pulls it out of the claim pool
 * and the marker's own queue without unclaiming it, so the marker identity
 * and any marking already done are preserved.
 */
export const flagSubmissionForReview = async (submissionId, markerId, markerName, comment) => {
  try {
    const docRef = doc(db, 'submissions', submissionId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Submission not found');
    }

    const previousStatus = docSnap.data().status;

    await updateDoc(docRef, {
      status: 'flagged_for_review',
      previousStatus,
      flaggedBy: markerId,
      flaggedByName: markerName,
      flagComment: comment,
      flaggedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Flag submission error:', error);
    throw error;
  }
};

/**
 * Head marker: return a flagged submission to the marker who flagged it.
 * Always lands back in 'marking' so the marker re-opens it in the marking
 * screen and sees any newly added pages, regardless of how far marking had
 * got before it was flagged.
 */
export const returnFlaggedToMarker = async (submissionId) => {
  try {
    const docRef = doc(db, 'submissions', submissionId);
    await updateDoc(docRef, {
      status: 'marking'
    });
    return true;
  } catch (error) {
    console.error('Return flagged submission error:', error);
    throw error;
  }
};

/**
 * Head marker: release a flagged submission back to the general available
 * pool for any marker to claim (e.g. the original marker is unavailable).
 */
export const releaseFlaggedToPool = async (submissionId) => {
  try {
    const docRef = doc(db, 'submissions', submissionId);
    await updateDoc(docRef, {
      status: 'pending',
      assignedTo: null,
      markerName: null,
      claimedBy: null,
      claimedAt: null
    });
    return true;
  } catch (error) {
    console.error('Release flagged submission error:', error);
    throw error;
  }
};

/**
 * Get all submissions currently flagged for head marker review
 */
export const getFlaggedSubmissions = async () => {
  try {
    const orgId = await getCurrentUserOrgId();
    const q = query(
      collection(db, 'submissions'),
      where('orgId', '==', orgId),
      where('status', '==', 'flagged_for_review')
    );

    const snapshot = await getDocs(q);
    const submissions = [];

    snapshot.forEach((doc) => {
      submissions.push({
        id: doc.id,
        ...doc.data()
      });
    });

    submissions.sort((a, b) => {
      const aTime = a.flaggedAt?.toDate?.() || new Date(0);
      const bTime = b.flaggedAt?.toDate?.() || new Date(0);
      return bTime - aTime;
    });

    return submissions;
  } catch (error) {
    console.error('Get flagged submissions error:', error);
    throw error;
  }
};

/**
 * Get all submissions with a pending addition awaiting head marker release.
 * A pending addition is a late upload against a submission that's already
 * flagged/assigned/marking/awaiting_review/marked/rejected (see createSubmission's quarantine
 * branch) - held here until releasePendingAdditions merges it in.
 */
export const getSubmissionsWithPendingAdditions = async () => {
  try {
    const orgId = await getCurrentUserOrgId();
    const q = query(
      collection(db, 'submissions'),
      where('orgId', '==', orgId),
      where('hasPendingAdditions', '==', true)
    );

    const snapshot = await getDocs(q);
    const submissions = [];

    snapshot.forEach((doc) => {
      submissions.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return submissions;
  } catch (error) {
    console.error('Get submissions with pending additions error:', error);
    throw error;
  }
};

/**
 * Head marker: release a quarantined pending addition. Appends the queued
 * file(s) to releasedAdditionFilePaths/releasedAdditionFileNames - a
 * separate, always-appended-at-the-end field, NEVER originalFilePaths - so
 * pages already marked keep their exact existing order and page numbers.
 * Routes the submission through the existing flagged-for-review flow so the
 * head marker can then use the normal "Return to marker" action.
 */
export const releasePendingAdditions = async (submissionId) => {
  try {
    const docRef = doc(db, 'submissions', submissionId);

    await runTransaction(db, async (tx) => {
      const docSnap = await tx.get(docRef);
      if (!docSnap.exists()) {
        throw new Error('Submission not found');
      }

      const data = docSnap.data();
      const pendingPaths = data.pendingAdditionFilePaths || [];
      const pendingNames = data.pendingAdditionFileNames || [];

      if (pendingPaths.length === 0) {
        throw new Error('No pending additions to release');
      }

      tx.update(docRef, {
        releasedAdditionFilePaths: [...(data.releasedAdditionFilePaths || []), ...pendingPaths],
        releasedAdditionFileNames: [...(data.releasedAdditionFileNames || []), ...pendingNames],
        fileCount: (data.fileCount || 0) + pendingPaths.length,
        hasPendingAdditions: false,
        pendingAdditionFilePaths: [],
        pendingAdditionFileNames: [],
        pendingAdditionAt: null,
        status: 'flagged_for_review',
        previousStatus: data.status,
        flaggedBy: data.assignedTo || null,
        flaggedByName: data.markerName || 'Marker',
        flagComment: `Student added ${pendingPaths.length} additional page${pendingPaths.length > 1 ? 's' : ''} after marking started. It has been added to the end of the file, so marking already done on the earlier pages is unaffected — please mark the new page${pendingPaths.length > 1 ? 's' : ''} and finalise.`,
        flaggedAt: serverTimestamp()
      });
    });

    return true;
  } catch (error) {
    console.error('Release pending additions error:', error);
    throw error;
  }
};

/**
 * Get submissions for a student
 */
export const getStudentSubmissions = async (studentId) => {
  try {
    // orgId filter required even though studentId alone would return the
    // right documents - Firestore evaluates a list query's safety from the
    // query's own filters, not the data it returns. A marker/head-marker
    // viewing another student's history only satisfies the org-scoped
    // marker read rule (see firestore.rules), and that rule requires
    // orgId - without this filter Firestore can't prove the query is safe
    // under it and rejects the whole query outright ("Missing or
    // insufficient permissions"), same bug class as the earlier
    // getAllCohorts() cross-org fix. A student's own "My Submissions" call
    // (studentId === their own uid) happened to keep working throughout,
    // since that path is separately covered by the studentId-only rule.
    const orgId = await getCurrentUserOrgId();
    const q = query(
      collection(db, 'submissions'),
      where('orgId', '==', orgId),
      where('studentId', '==', studentId),
      orderBy('submittedAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const submissions = [];
    
    snapshot.forEach((doc) => {
      submissions.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return submissions;
  } catch (error) {
    console.error('Get student submissions error:', error);
    throw error;
  }
};

/**
 * Get all claimed submissions (for head marker to see what all markers are working on)
 */
export const getAllClaimedSubmissions = async (cohortId = null) => {
  try {
    const orgId = await getCurrentUserOrgId();
    let q;

    if (cohortId) {
      q = query(
        collection(db, 'submissions'),
        where('orgId', '==', orgId),
        where('cohortId', '==', cohortId),
        where('status', 'in', ['assigned', 'marking'])
      );
    } else {
      q = query(
        collection(db, 'submissions'),
        where('orgId', '==', orgId),
        where('status', 'in', ['assigned', 'marking'])
      );
    }
    
    const snapshot = await getDocs(q);
    const submissions = [];
    
    snapshot.forEach((doc) => {
      submissions.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort by claimed time (most recent first)
    submissions.sort((a, b) => {
      const aTime = a.claimedAt?.toDate?.() || new Date(0);
      const bTime = b.claimedAt?.toDate?.() || new Date(0);
      return bTime - aTime;
    });
    
    return submissions;
  } catch (error) {
    console.error('Get all claimed submissions error:', error);
    throw error;
  }
};

/**
 * Get pending submissions for markers (only their assigned/claimed submissions)
 */
export const getPendingSubmissions = async (markerId = null, cohortId = null) => {
  try {
    const orgId = await getCurrentUserOrgId();
    let q;

    // If markerId is provided, only get submissions assigned to this marker
    if (markerId) {
      if (cohortId) {
        // Remove orderBy to avoid needing composite index - we'll sort in JS
        q = query(
          collection(db, 'submissions'),
          where('orgId', '==', orgId),
          where('cohortId', '==', cohortId),
          where('assignedTo', '==', markerId),
          where('status', 'in', ['assigned', 'marking', 'rejected'])
        );
      } else {
        // Remove orderBy to avoid needing composite index - we'll sort in JS
        q = query(
          collection(db, 'submissions'),
          where('orgId', '==', orgId),
          where('assignedTo', '==', markerId),
          where('status', 'in', ['assigned', 'marking', 'rejected'])
        );
      }
    } else {
      // Fallback for backward compatibility (get all pending, assigned, marking)
      if (cohortId) {
        q = query(
          collection(db, 'submissions'),
          where('orgId', '==', orgId),
          where('cohortId', '==', cohortId),
          where('status', 'in', ['pending', 'assigned', 'marking']),
          orderBy('submittedAt', 'asc')
        );
      } else {
        q = query(
          collection(db, 'submissions'),
          where('orgId', '==', orgId),
          where('status', 'in', ['pending', 'assigned', 'marking']),
          orderBy('submittedAt', 'asc')
        );
      }
    }
    
    const snapshot = await getDocs(q);
    const submissions = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      
      // Calculate hours since submission
      const now = new Date();
      const submittedAt = data.submittedAt?.toDate();
      const hoursSinceSubmission = submittedAt ? 
        (now - submittedAt) / (1000 * 60 * 60) : 0;
      
      // Calculate hours until deadline
      const deadline = data.deadline?.toDate();
      const hoursUntilDeadline = deadline ?
        (deadline - now) / (1000 * 60 * 60) : 48;
      
      submissions.push({
        id: doc.id,
        ...data,
        hoursSinceSubmission,
        hoursUntilDeadline,
        isOverdue: hoursUntilDeadline < 0,
        isUrgent: hoursUntilDeadline < 12 && hoursUntilDeadline > 0
      });
    });
    
    // Sort by urgency first, then by submission time
    submissions.sort((a, b) => {
      // Priority 1: Overdue items first
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;
      
      // Priority 2: Urgent items next
      if (a.isUrgent && !b.isUrgent) return -1;
      if (!a.isUrgent && b.isUrgent) return 1;
      
      // Priority 3: Sort by submission time (oldest first)
      const aTime = a.submittedAt?.toDate?.() || new Date(0);
      const bTime = b.submittedAt?.toDate?.() || new Date(0);
      return aTime - bTime;
    });
    
    return submissions;
  } catch (error) {
    console.error('Get pending submissions error:', error);
    throw error;
  }
};

/**
 * Get marked submissions for markers
 */
export const getMarkedSubmissions = async (cohortId = null) => {
  try {
    const orgId = await getCurrentUserOrgId();
    let q;

    if (cohortId) {
      q = query(
        collection(db, 'submissions'),
        where('orgId', '==', orgId),
        where('cohortId', '==', cohortId),
        where('status', '==', 'marked'),
        orderBy('markedAt', 'desc')
      );
    } else {
      q = query(
        collection(db, 'submissions'),
        where('orgId', '==', orgId),
        where('status', '==', 'marked'),
        orderBy('markedAt', 'desc')
      );
    }
    
    const snapshot = await getDocs(q);
    const submissions = [];
    
    snapshot.forEach((doc) => {
      submissions.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return submissions;
  } catch (error) {
    console.error('Get marked submissions error:', error);
    throw error;
  }
};

/**
 * Get a single submission by ID
 */
export const getSubmission = async (submissionId) => {
  try {
    const docRef = doc(db, 'submissions', submissionId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Submission not found');
    }
    
    return {
      id: docSnap.id,
      ...docSnap.data()
    };
  } catch (error) {
    console.error('Get submission error:', error);
    throw error;
  }
};

/**
 * Assign submission to a marker
 */
export const assignSubmission = async (submissionId, markerId) => {
  try {
    const docRef = doc(db, 'submissions', submissionId);
    await updateDoc(docRef, {
      assignedTo: markerId,
      status: 'assigned'
    });
  } catch (error) {
    console.error('Assign submission error:', error);
    throw error;
  }
};

/**
 * Get all pending submissions (for open pool)
 */
export const getAllPendingSubmissions = async () => {
  try {
    const orgId = await getCurrentUserOrgId();
    const q = query(
      collection(db, 'submissions'),
      where('orgId', '==', orgId),
      where('status', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(q);
    const submissions = [];
    
    querySnapshot.forEach((doc) => {
      submissions.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort by submission date (oldest first)
    submissions.sort((a, b) => {
      const aTime = a.submittedAt?.toDate?.() || new Date(0);
      const bTime = b.submittedAt?.toDate?.() || new Date(0);
      return aTime - bTime;
    });
    
    return submissions;
  } catch (error) {
    console.error('Get pending submissions error:', error);
    throw error;
  }
};

/**
 * Get count of submissions marker has claimed for a specific homework type
 */
export const getMarkerSubmissionCount = async (markerId, homeworkType) => {
  try {
    const orgId = await getCurrentUserOrgId();
    const q = query(
      collection(db, 'submissions'),
      where('orgId', '==', orgId),
      where('assignedTo', '==', markerId),
      where('homeworkType', '==', homeworkType),
      where('status', 'in', ['marking', 'awaiting-review'])
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Get marker submission count error:', error);
    return 0;
  }
};

/**
 * Claim a submission (with cap checking)
 */
export const claimSubmission = async (submissionId, markerId, markerName, homeworkType, cap = null) => {
  try {
    // Check if cap is set and if marker is at cap
    if (cap !== null && cap !== undefined) {
      const currentCount = await getMarkerSubmissionCount(markerId, homeworkType);
      if (currentCount >= cap) {
        throw new Error(`You have reached the maximum of ${cap} submissions for this homework type`);
      }
    }
    
    // Get the submission
    const docRef = doc(db, 'submissions', submissionId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Submission not found');
    }
    
    const submission = docSnap.data();

// Check if still pending
if (submission.status !== 'pending') {
  throw new Error('This submission has already been claimed by another marker');
}

// Check if already claimed by someone else (double-claim prevention)
if (submission.assignedTo && submission.assignedTo !== markerId) {
  throw new Error('This submission has already been claimed by another marker');
}

// Claim the submission
await updateDoc(docRef, {
  status: 'marking',
  assignedTo: markerId,
  markerName: markerName,
  claimedBy: markerId, // Store who claimed it for visibility
  claimedAt: serverTimestamp()
});
    
    return true;
  } catch (error) {
    console.error('Claim submission error:', error);
    throw error;
  }
};

/**
 * Update submission status to marking
 */
export const startMarking = async (submissionId, markerId) => {
  try {
    const docRef = doc(db, 'submissions', submissionId);
    await updateDoc(docRef, {
      status: 'marking',
      assignedTo: markerId
    });
  } catch (error) {
    console.error('Start marking error:', error);
    throw error;
  }
};

/**
 * Complete marking - save marking data and set status
 * PDF creation happens later when head marker approves
 */
export const completeMarking = async (submissionId, markingData) => {
  try {
    // Get submission to check review requirement
    const submission = await getSubmission(submissionId);
    
    // Calculate turnaround time
    const now = new Date();
    const submittedAt = submission.submittedAt?.toDate();
    const turnaroundHours = submittedAt ? 
      (now - submittedAt) / (1000 * 60 * 60) : null;
    
    // Check if this homework type requires review
    const { requiresReview } = await import('./settings');
    const needsReview = await requiresReview(submission.homeworkType);
    
    // Set status based on review requirement
    const newStatus = needsReview ? 'awaiting_review' : 'marked';
    
    // Save marking data to Firestore
    await saveMarkingData(submissionId, markingData);
    
    // Update submission with status and scores (but NO marked PDF yet if needs review)
    const docRef = doc(db, 'submissions', submissionId);
    const updateData = {
      status: newStatus,
      markedAt: serverTimestamp(),
      turnaroundHours: turnaroundHours ? Math.round(turnaroundHours * 10) / 10 : null,
      score: markingData.score
    };
    
    // Only set markedFileUrl if going direct to student (no review)
    if (!needsReview) {
      // If no review needed, we need to generate PDF here
      // For now, we'll handle this separately
      updateData.markedFileUrl = null; // Will be generated by approveSubmission
    }
    
    await updateDoc(docRef, updateData);
    
    // Increment marker statistics for payment tracking (both marker and head marker views).
    // Not again for a homework a head marker sent back after it was marked
    // (sendMarkedBackToMarker) - it was already counted the first time.
    if (submission.markerStatsCounted) {
      console.log('Marker stats already counted for this homework - not counting again');
    } else if (submission.assignedTo) {
      console.log('=== INCREMENTING MARKER STATS ===');
      console.log('Marker ID:', submission.assignedTo);
      console.log('Homework Type:', submission.homeworkType);
      await incrementMarkerStats(submission.assignedTo, submission.homeworkType, submission.orgId);
      console.log('Stats incremented successfully (both marker and head marker views)');
      console.log('=================================');
    } else {
      console.warn('No assignedTo found - stats not incremented');
    }
    
    console.log('Marking completed for:', submissionId, '- Status:', newStatus);
  } catch (error) {
    console.error('Complete marking error:', error);
    throw error;
  }
};

/**
 * Save marking data (marks, annotations, etc.)
 */
export const saveMarkingData = async (submissionId, markingData) => {
  try {
    const docRef = doc(db, 'markingData', submissionId);
    const dataToSave = {
      ...markingData,
      savedAt: serverTimestamp()
    };
    
    // Use setDoc with merge to create or update
    await setDoc(docRef, dataToSave, { merge: true });
  } catch (error) {
    console.error('Save marking data error:', error);
    throw error;
  }
};

/**
 * Get marking data for a submission
 */
export const getMarkingData = async (submissionId) => {
  try {
    const docRef = doc(db, 'markingData', submissionId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    return docSnap.data();
  } catch (error) {
    console.error('Get marking data error:', error);
    return null;
  }
};

/**
 * Get all submissions for a cohort (for analytics)
 */
export const getCohortSubmissions = async (cohortId) => {
  try {
    const orgId = await getCurrentUserOrgId();
    const q = query(
      collection(db, 'submissions'),
      where('orgId', '==', orgId),
      where('cohortId', '==', cohortId),
      orderBy('submittedAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const submissions = [];
    
    snapshot.forEach((doc) => {
      submissions.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return submissions;
  } catch (error) {
    console.error('Get cohort submissions error:', error);
    throw error;
  }
};

/**
 * Soft delete a submission (for students - marks as deleted but keeps for head marker)
 */
export const softDeleteSubmission = async (submissionId) => {
  try {
    const docRef = doc(db, 'submissions', submissionId);
    await updateDoc(docRef, {
      deletedByStudent: true,
      deletedByStudentAt: serverTimestamp()
    });
    
    console.log('Submission soft deleted:', submissionId);
  } catch (error) {
    console.error('Soft delete submission error:', error);
    throw error;
  }
};

/**
 * Permanently delete a submission (for head marker - removes everything including files)
 */
export const permanentlyDeleteSubmission = async (submissionId) => {
  try {
    const submission = await getSubmission(submissionId);
    
    // Delete all original files from storage (handle both paths and URLs)
    const filePaths = submission.originalFilePaths || submission.originalFileUrls || [];
    if (filePaths.length > 0) {
      for (const filePath of filePaths) {
        try {
          const originalRef = ref(storage, filePath);
          await deleteObject(originalRef);
        } catch (error) {
          console.log('Original file already deleted or not found:', filePath);
        }
      }
    } else if (submission.originalFileUrl) {
      // Handle old single-file format
      try {
        const originalRef = ref(storage, submission.originalFileUrl);
        await deleteObject(originalRef);
      } catch (error) {
        console.log('Original file already deleted');
      }
    }
    
    // Delete marked file from storage (handle both path and URL)
    const markedPath = submission.markedFilePath || submission.markedFileUrl;
    if (markedPath) {
      try {
        const markedRef = ref(storage, markedPath);
        await deleteObject(markedRef);
      } catch (error) {
        console.log('Marked file already deleted');
      }
    }

    // Delete any quarantined/released addition files too
    const additionPaths = [
      ...(submission.pendingAdditionFilePaths || []),
      ...(submission.releasedAdditionFilePaths || [])
    ];
    for (const filePath of additionPaths) {
      try {
        const additionRef = ref(storage, filePath);
        await deleteObject(additionRef);
      } catch (error) {
        console.log('Addition file already deleted or not found:', filePath);
      }
    }

    // Delete submission document
    const docRef = doc(db, 'submissions', submissionId);
    await deleteDoc(docRef);

    console.log('Submission permanently deleted:', submissionId);

  } catch (error) {
    console.error('Permanently delete submission error:', error);
    throw error;
  }
};

/**
 * Head marker: reopen a typed (exam-window) submission so the student can
 * keep editing it with all their answers. Nothing is deleted - see
 * reopenExamSubmission in functions/index.js.
 */
export const reopenExamSubmission = async (submissionId) => {
  await httpsCallable(getFunctions(getApp()), 'reopenExamSubmission')({ submissionId });
};

/**
 * Reset a submission for resubmission (for head marker - use for broken/corrupted files).
 * Unlike permanentlyDeleteSubmission, this keeps the submission document (preserving the
 * audit trail) but deletes the files and flips status back to 'pending' so the student can
 * re-upload through the normal flow - createSubmission will find this doc via its existing
 * pending-submission lookup and slot the new file straight in.
 */
export const resetSubmissionForResubmit = async (submissionId, reason, resetByName) => {
  try {
    const submission = await getSubmission(submissionId);

    // Delete all original files from storage (handle both paths and URLs)
    const filePaths = submission.originalFilePaths || submission.originalFileUrls || [];
    if (filePaths.length > 0) {
      for (const filePath of filePaths) {
        try {
          const originalRef = ref(storage, filePath);
          await deleteObject(originalRef);
        } catch (error) {
          console.log('Original file already deleted or not found:', filePath);
        }
      }
    } else if (submission.originalFileUrl) {
      try {
        const originalRef = ref(storage, submission.originalFileUrl);
        await deleteObject(originalRef);
      } catch (error) {
        console.log('Original file already deleted');
      }
    }

    // Delete marked file from storage (handle both path and URL)
    const markedPath = submission.markedFilePath || submission.markedFileUrl;
    if (markedPath) {
      try {
        const markedRef = ref(storage, markedPath);
        await deleteObject(markedRef);
      } catch (error) {
        console.log('Marked file already deleted');
      }
    }

    // Delete any quarantined/released addition files too (see createSubmission's
    // quarantine branch and releasePendingAdditions) - a reset must not leave
    // stale addition pages that would resurface on the fresh submission
    const additionPaths = [
      ...(submission.pendingAdditionFilePaths || []),
      ...(submission.releasedAdditionFilePaths || [])
    ];
    for (const filePath of additionPaths) {
      try {
        const additionRef = ref(storage, filePath);
        await deleteObject(additionRef);
      } catch (error) {
        console.log('Addition file already deleted or not found:', filePath);
      }
    }

    // Clean up orphaned marking data (permanentlyDeleteSubmission never did this either)
    try {
      await deleteDoc(doc(db, 'markingData', submissionId));
    } catch (error) {
      console.log('No markingData to delete for:', submissionId);
    }

    const docRef = doc(db, 'submissions', submissionId);
    await updateDoc(docRef, {
      status: 'pending',
      originalFilePaths: [],
      fileNames: [],
      fileCount: 0,
      assignedTo: null,
      markerName: null,
      claimedBy: null,
      claimedAt: null,
      markedAt: null,
      markedFilePath: null,
      markedSpreadsheet: null,
      turnaroundHours: null,
      score: null,
      reviewedAt: null,
      reviewStatus: null,
      deletedByStudent: false,
      deletedByStudentAt: null,
      hasPendingAdditions: false,
      pendingAdditionFilePaths: [],
      pendingAdditionFileNames: [],
      pendingAdditionAt: null,
      releasedAdditionFilePaths: [],
      releasedAdditionFileNames: [],
      resetReason: reason || null,
      resetAt: serverTimestamp(),
      resetBy: resetByName || null
    });

    console.log('Submission reset for resubmission:', submissionId);

  } catch (error) {
    console.error('Reset submission error:', error);
    throw error;
  }
};

/**
 * Reclassify a submission to a different homework type, keeping the uploaded
 * file(s) - for when a student uploaded the right file into the wrong slot
 * (e.g. their TH2 answer submitted under TH1). Unlike resetSubmissionForResubmit,
 * this never touches Storage or the student - the marking scheme, comment
 * bank and section structure shown to whoever marks it are looked up live by
 * homeworkType, so relabeling the doc is enough to make it mark correctly.
 *
 * Only allowed while the submission is still 'pending' (unclaimed) - once a
 * marker has claimed or started marking it, unclaim it first (or use Reset)
 * rather than changing the type out from under them.
 */
export const changeSubmissionHomeworkType = async (submissionId, newHomeworkType, changedByName) => {
  try {
    const docRef = doc(db, 'submissions', submissionId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      throw new Error('Submission not found');
    }

    const data = docSnap.data();
    if (data.status !== 'pending') {
      throw new Error('Only an unclaimed (pending) submission can be reclassified - unclaim it first.');
    }

    const oldHomeworkType = data.homeworkType;
    if (newHomeworkType === oldHomeworkType) {
      throw new Error('That is already this submission\'s homework type.');
    }

    // Guard against landing on top of a real submission the student already
    // has for the target type
    const existingQuery = query(
      collection(db, 'submissions'),
      where('studentId', '==', data.studentId),
      where('homeworkType', '==', newHomeworkType),
      where('cohortId', '==', data.cohortId),
      where('orgId', '==', data.orgId || DEFAULT_ORG_ID)
    );
    const existingSnapshot = await getDocs(existingQuery);
    const hasConflict = existingSnapshot.docs.some(d => (d.data().fileCount || 0) > 0);
    if (hasConflict) {
      throw new Error('This student already has a submission for that homework type. Resolve that one first.');
    }

    // Re-derive deadline/lateness against the new type's own deadline, rather
    // than leaving the old type's snapshot sitting on the doc
    let homeworkDeadline = null;
    let isLate = false;
    let minutesLate = 0;
    try {
      const deadlineDoc = await getDoc(doc(db, 'cohorts', data.cohortId, 'deadlines', 'current'));
      if (deadlineDoc.exists()) {
        homeworkDeadline = deadlineDoc.data()[newHomeworkType] || null;
        if (homeworkDeadline && data.submittedAt) {
          const deadlineTime = homeworkDeadline.toMillis();
          const submittedTime = data.submittedAt.toMillis();
          if (submittedTime > deadlineTime) {
            isLate = true;
            minutesLate = Math.floor((submittedTime - deadlineTime) / (1000 * 60));
          }
        }
      }
    } catch (deadlineError) {
      console.warn('Could not re-derive deadline for reclassified submission:', deadlineError);
    }

    await updateDoc(docRef, {
      homeworkType: newHomeworkType,
      homeworkDeadline,
      isLate,
      minutesLate,
      reclassifiedFrom: oldHomeworkType,
      reclassifiedAt: serverTimestamp(),
      reclassifiedBy: changedByName || null
    });

    console.log(`Submission ${submissionId} reclassified: ${oldHomeworkType} -> ${newHomeworkType}`);
  } catch (error) {
    console.error('Change submission homework type error:', error);
    throw error;
  }
};

/**
 * Delete a submission (DEPRECATED - use softDeleteSubmission for students or permanentlyDeleteSubmission for head marker)
 * Kept for backwards compatibility
 */
export const deleteSubmission = async (submissionId) => {
  // Default to soft delete for safety
  return softDeleteSubmission(submissionId);
};

/**
 * Get submissions awaiting review (for head marker)
 */
export const getSubmissionsAwaitingReview = async (cohortId = null) => {
  try {
    const orgId = await getCurrentUserOrgId();
    let q;

    if (cohortId) {
      q = query(
        collection(db, 'submissions'),
        where('orgId', '==', orgId),
        where('cohortId', '==', cohortId),
        where('status', '==', 'awaiting_review'),
        orderBy('markedAt', 'desc')
      );
    } else {
      q = query(
        collection(db, 'submissions'),
        where('orgId', '==', orgId),
        where('status', '==', 'awaiting_review'),
        orderBy('markedAt', 'desc')
      );
    }
    
    const snapshot = await getDocs(q);
    const submissions = [];
    
    snapshot.forEach((doc) => {
      submissions.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return submissions;
  } catch (error) {
    console.error('Get submissions awaiting review error:', error);
    throw error;
  }
};

/**
 * Upload the original .xlsx behind a "Spreadsheet Upload" submission (see
 * generateSpreadsheetUploadPdf.js - the submission's actual file is the
 * generated PDF; this keeps the student's real workbook available too, for
 * a marker who wants to open it in real Excel).
 */
export const uploadSpreadsheetSource = async (submissionId, spreadsheetFile) => {
  try {
    const submission = await getSubmission(submissionId);

    const uniqueSuffix = doc(collection(db, 'submissions')).id;
    const filePath = `submissions/${submission.cohortId}/${submission.studentId}/${submission.homeworkType}_source_${uniqueSuffix}.xlsx`;
    const fileRef = ref(storage, filePath);
    await uploadBytes(fileRef, spreadsheetFile);

    const docRef = doc(db, 'submissions', submissionId);
    await updateDoc(docRef, { spreadsheetSourceFilePath: filePath });

    return filePath;
  } catch (error) {
    console.error('Upload spreadsheet source error:', error);
    throw error;
  }
};

/**
 * Upload marked PDF file to Firebase Storage
 */
// markedSpreadsheet (optional) - the student's copy of any spreadsheet
// marking (utils/studentSpreadsheetMarking.js), saved with the marked PDF so
// it reaches the student at the same moment
export const uploadMarkedPDF = async (submissionId, markedPdfFile, markedSpreadsheet = null) => {
  try {
    const submission = await getSubmission(submissionId);
    
    // Upload marked PDF to Firebase Storage
    const filePath = `marked/${submission.cohortId}/${submission.studentId}/${submission.homeworkType}_marked.pdf`;
    const fileRef = ref(storage, filePath);
    await uploadBytes(fileRef, markedPdfFile);
    
    // Store the FILE PATH (not URL) - security rules will be enforced
    const docRef = doc(db, 'submissions', submissionId);
    await updateDoc(docRef, {
      markedFilePath: filePath,
      ...(markedSpreadsheet ? { markedSpreadsheet } : {})
    });
    
    console.log('Marked PDF uploaded for:', submissionId);
    return filePath;
  } catch (error) {
    console.error('Upload marked PDF error:', error);
    throw error;
  }
};

/**
 * Approve submission (head marker approves marking and creates final PDF)
 */
export const approveSubmission = async (submissionId, scoreData = null) => {
  try {
    // Note: PDF creation and upload handled by component
    // This function updates the status and score (if provided by head marker)
    const docRef = doc(db, 'submissions', submissionId);
    const updateData = {
      status: 'marked',
      reviewedAt: serverTimestamp(),
      reviewStatus: 'approved'
    };
    
    // If head marker edited scores, update them
    if (scoreData) {
      updateData.score = scoreData;
    }
    
    await updateDoc(docRef, updateData);
    
    console.log('Submission approved:', submissionId, scoreData ? '(with updated scores)' : '');
  } catch (error) {
    console.error('Approve submission error:', error);
    throw error;
  }
};

/**
 * Persist the head marker's edits to the AI-generated summary (see
 * generateAiSummaryForSubmission in functions/index.js). Does not touch
 * aiSummaryStatus - editing an already-generated summary doesn't change
 * whether generation succeeded.
 */
export const updateAiSummaryText = async (submissionId, text) => {
  try {
    const docRef = doc(db, 'submissions', submissionId);
    await updateDoc(docRef, { aiSummary: text, aiSummaryEditedAt: serverTimestamp() });
  } catch (error) {
    console.error('Update AI summary text error:', error);
    throw error;
  }
};

/**
 * Reject submission (send back to marker with feedback)
 */
export const updateSubmissionStatus = async (submissionId, status) => {
  try {
    const docRef = doc(db, 'submissions', submissionId);
    await updateDoc(docRef, { status });
  } catch (error) {
    console.error('Update submission status error:', error);
    throw error;
  }
};

/**
 * Head marker sends an already-marked homework back to its marker (e.g.
 * marking was finished by mistake). It goes into the marker's queue as
 * "returned for revision" with the note, their annotations kept (markingData
 * is untouched), and leaves the student's dashboard until it's finished
 * again. markerStatsCounted stops completeMarking counting it a second time.
 */
export const sendMarkedBackToMarker = async (submissionId, notes, sentBackByName) => {
  const docRef = doc(db, 'submissions', submissionId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) throw new Error('Submission not found');
  const data = snap.data();
  if (data.status !== 'marked') throw new Error('Only a marked homework can be sent back to its marker.');
  if (!data.assignedTo) throw new Error('This homework has no marker recorded, so it cannot be sent back.');
  await updateDoc(docRef, {
    status: 'rejected',
    reviewStatus: 'sent_back',
    rejectionNotes: notes,
    sentBackAt: serverTimestamp(),
    sentBackBy: sentBackByName || null,
    markerStatsCounted: true
  });
  return data;
};

export const rejectSubmission = async (submissionId, rejectionNotes) => {
  try {
    const docRef = doc(db, 'submissions', submissionId);
    await updateDoc(docRef, {
      status: 'rejected',
      reviewedAt: serverTimestamp(),
      reviewStatus: 'rejected',
      rejectionNotes,
      // Clear marked data so marker re-marks from scratch
      markedAt: null,
      markedFileUrl: null,
      score: null
    });
    console.log('Submission rejected:', submissionId);
  } catch (error) {
    console.error('Reject submission error:', error);
    throw error;
  }
};

/**
 * Get authenticated download URL from file path
 * This respects Firebase Storage security rules
 */
export const getAuthenticatedFileUrl = async (filePath) => {
  try {
    if (!filePath) return null;
    
    const fileRef = ref(storage, filePath);
    const url = await getDownloadURL(fileRef);
    return url;
  } catch (error) {
    console.error('Get authenticated file URL error:', error);
    // If permission denied, return null
    if (error.code === 'storage/unauthorized') {
      console.warn('Access denied to file:', filePath);
      return null;
    }
    throw error;
  }
};